import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { PrismBanner, PrismButton, PrismDialog } from "@prism-ui/react";
import { toast } from "../../../Toaster/toast";
import { apiError } from "./RatingWindowTab";
import WordMap from "./WordMap";
import CohortSummary, { ReviewProgress } from "./CohortSummary";
import useConfirm from "../../../../hooks/useConfirm";
import SubmittedApp from "../../../Application/SubmittedApp/SubmittedApp";
import { blindMaskFrom, type BlindConfig } from "../../../../utils/blindMask";

export type Track = "mentee" | "mentor" | "committee";
const TRACKS: { id: Track; label: string }[] = [
  { id: "mentee", label: "Mentees" },
  { id: "mentor", label: "Mentors" },
  { id: "committee", label: "Committee Members" },
];
// Each track is its own gated step: every applicant must be decided before moving on.
const FIRST_TRACK_STEP = 3;
export const STEPS = ["Mode", "Seats", "Visibility", ...TRACKS.map((t) => t.label), "HR Review handoff"];
const FINISH_STEP = STEPS.length - 1;
const trackAtStep = (step: number): Track | null => TRACKS[step - FIRST_TRACK_STEP]?.id ?? null;

type Decision = "accepted" | "rejected";

// What Host Review passes to HR Eligibility Review (OpenSpec #44): the proposed cohort per track,
// and the seat targets it was filled against (available null = no limit).
export interface Handoff {
  seats?: Record<Track, { available: number | null; waitlist: number }>;
  tracks: Record<Track, { accepted: number[]; waitlisted: { application_id: number }[]; rejected: number }>;
  totals: { accepted: number; waitlisted: number; rejected: number };
}

type Seat = "accepted" | "waitlisted";
const STATUS_ACCEPTED = 4;
const STATUS_WAITLISTED = 8;

// Seats filled in one track. A formal review counts live application statuses, exactly as the
// server does (an applicant Accepted before this review still holds a seat); a demo counts the
// seats it handed out in the browser.
export const seatTally = (
  queue: { application_id: number; decision: Decision | null; status_id?: number | null }[],
  seatOf: Record<number, Seat> | null
) => {
  let accepted = 0;
  let waitlisted = 0;
  for (const q of queue) {
    const seat = seatOf
      ? q.decision === "accepted"
        ? seatOf[q.application_id]
        : undefined
      : q.status_id === STATUS_ACCEPTED
        ? "accepted"
        : q.status_id === STATUS_WAITLISTED
          ? "waitlisted"
          : undefined;
    if (seat === "accepted") accepted += 1;
    else if (seat === "waitlisted") waitlisted += 1;
  }
  return { accepted, waitlisted };
};
const BACKUP_FIELDS = {
  mentee: "mentee_backup_seats",
  mentor: "mentor_backup_seats",
  committee: "tcm_backup_seats",
} as const;
type BackupField = (typeof BACKUP_FIELDS)[Track];
type SeatTargets = Pick<Session, "mentee_quota" | "mentor_quota" | "tcm_quota" | BackupField>;
const availableSeats = (s: SeatTargets, t: Track): number | null =>
  ({ mentee: s.mentee_quota, mentor: s.mentor_quota, committee: s.tcm_quota })[t] ?? null;
const backupSeats = (s: SeatTargets, t: Track): number => s[BACKUP_FIELDS[t]] || 0;

// Mirrors the server: an accept takes a primary seat while any remain, then a backup (waitlist)
// seat, then is refused. An applicant already seated keeps their seat.
export const routeAccept = (
  id: number,
  track: Track,
  seatOf: Record<number, Seat>,
  trackOf: Record<number, Track>,
  targets: SeatTargets
): Seat | null => {
  if (seatOf[id]) return seatOf[id];
  const inTrack = Object.entries(seatOf).filter(([other]) => trackOf[Number(other)] === track);
  const accepted = inTrack.filter(([, seat]) => seat === "accepted").length;
  const waitlisted = inTrack.length - accepted;
  const available = availableSeats(targets, track);
  if (available === null || accepted < available) return "accepted";
  if (waitlisted < backupSeats(targets, track)) return "waitlisted";
  return null;
};

export interface SeatMove {
  application_id: number;
  track: Track;
  name: string;
  average: number | null;
  waitlist_position: number;
}
export interface SeatPlan {
  moved: SeatMove[];
  backups: Record<BackupField, number>;
  backup_raised: Track[];
}

// Mirrors the server's planSeatChange for a demo: a target below a track's accepted count moves the
// lowest-rated accepted (unrated lowest, ties to the later id) to the front of the waitlist, best first,
// and each track's waitlist seats rise to fit its waitlist.
export const planDemotions = (
  seatOf: Record<number, Seat>,
  trackOf: Record<number, Track>,
  infoOf: Record<number, { name: string; average: number | null }>,
  targets: SeatTargets
): SeatPlan => {
  const moved: SeatMove[] = [];
  const backups = Object.fromEntries(
    TRACKS.map(({ id }) => [BACKUP_FIELDS[id], backupSeats(targets, id)])
  ) as Record<BackupField, number>;
  const raised: Track[] = [];
  for (const { id: track } of TRACKS) {
    const ids = Object.keys(seatOf)
      .map(Number)
      .filter((id) => trackOf[id] === track);
    const accepted = ids.filter((id) => seatOf[id] === "accepted");
    const waitlisted = ids.length - accepted.length;
    const available = availableSeats(targets, track);
    const excess = available === null ? 0 : Math.max(0, accepted.length - available);
    const avg = (id: number) => infoOf[id]?.average ?? -Infinity;
    const demoted = [...accepted]
      .sort((a, b) => avg(a) - avg(b) || b - a)
      .slice(0, excess)
      .reverse();
    demoted.forEach((id, i) =>
      moved.push({
        application_id: id,
        track,
        name: infoOf[id]?.name ?? `Application #${id}`,
        average: infoOf[id]?.average ?? null,
        waitlist_position: i + 1,
      })
    );
    const needed = waitlisted + demoted.length;
    if (needed > backups[BACKUP_FIELDS[track]]) {
      backups[BACKUP_FIELDS[track]] = needed;
      raised.push(track);
    }
  }
  return { moved, backups, backup_raised: raised };
};

// A demo writes no statuses, so its preview comes from the decisions made in the browser.
export const demoHandoff = (
  decisions: Record<number, Decision>,
  trackOf: Record<number, Track>,
  seatOf: Record<number, Seat> = {},
  targets?: SeatTargets
): Handoff => {
  const tracks = {} as Handoff["tracks"];
  for (const { id } of TRACKS) tracks[id] = { accepted: [], waitlisted: [], rejected: 0 };
  for (const [id, decision] of Object.entries(decisions)) {
    const bucket = tracks[trackOf[Number(id)]];
    if (!bucket) continue;
    if (decision === "rejected") bucket.rejected += 1;
    else if (seatOf[Number(id)] === "waitlisted") bucket.waitlisted.push({ application_id: Number(id) });
    else bucket.accepted.push(Number(id));
  }
  const sum = (pick: (b: Handoff["tracks"][Track]) => number) =>
    TRACKS.reduce((n, { id }) => n + pick(tracks[id]), 0);
  return {
    ...(targets
      ? {
          seats: Object.fromEntries(
            TRACKS.map(({ id }) => [id, { available: availableSeats(targets, id), waitlist: backupSeats(targets, id) }])
          ) as Handoff["seats"],
        }
      : {}),
    tracks,
    totals: {
      accepted: sum((b) => b.accepted.length),
      waitlisted: sum((b) => b.waitlisted.length),
      rejected: sum((b) => b.rejected),
    },
  };
};

interface Session {
  id: number | string;
  mode: "formal" | "demo";
  year?: number;
  created_at?: string;
  mentee_quota: number | null;
  mentor_quota: number | null;
  tcm_quota: number | null;
  mentee_backup_seats: number;
  mentor_backup_seats: number;
  tcm_backup_seats: number;
  state: {
    blind?: BlindConfig;
    reveal?: RevealState;
    tracks?: Partial<Record<Track, { order?: number[] | null; decisions?: Record<string, string>; ended?: boolean }>>;
  };
}

// Where a formal review picks up: the first unfinished track, Visibility if no track has
// started yet, or the HR Review handoff once every track is finished.
export const resumeStep = (s: Session): number => {
  const tracks = s.state?.tracks ?? {};
  if (!TRACKS.some(({ id }) => tracks[id]?.order)) return 2;
  const open = TRACKS.findIndex(({ id }) => !tracks[id]?.ended);
  return open === -1 ? FINISH_STEP : FIRST_TRACK_STEP + open;
};

const decisionCount = (s: Session) =>
  TRACKS.reduce((n, { id }) => n + Object.keys(s.state?.tracks?.[id]?.decisions ?? {}).length, 0);

interface RevealState {
  on: boolean;
  history?: { on: boolean; by?: number; at: string; track?: string | null }[];
}

type MaskKey = Exclude<keyof BlindConfig, "enabled">;
const MASK_FIELDS: { key: MaskKey; label: string }[] = [
  { key: "mask_name", label: "Name" },
  { key: "mask_email", label: "Email" },
  { key: "mask_title", label: "Job title" },
  { key: "mask_department", label: "Department" },
  { key: "mask_manager", label: "Manager" },
  { key: "mask_hire_date", label: "Hire date" },
  { key: "mask_location", label: "Location" },
  { key: "mask_photo", label: "Photo" },
];

const sameBlind = (a: BlindConfig = {}, b: BlindConfig = {}) =>
  (["enabled", ...MASK_FIELDS.map((f) => f.key)] as (keyof BlindConfig)[]).every((k) => !!a[k] === !!b[k]);

interface QueueItem {
  application_id: number;
  position: number;
  label: string;
  name: string | null;
  average: number | null;
  rating_count: number;
  comments: { ranking_id: number; text: string }[];
  decision: Decision | null;
  // Formal only: the application's live status, which is what seat limits are counted against.
  status_id?: number | null;
  // Formal only: set when the applicant moved into this step from another track or chapter.
  moved_in?: MovedIn | null;
}

interface MovedIn {
  reason: "type_change" | "transfer";
  from_track: Track | null;
  from_chapter: string | null;
}

// Names nobody, so it is safe while names are masked.
const movedInLabel = (m: MovedIn) =>
  m.reason === "transfer"
    ? `Transferred from ${m.from_chapter ?? "another chapter"}`
    : `Moved in from ${TRACKS.find((t) => t.id === m.from_track)?.label ?? "another track"}`;

// An applicant who reached a completed review afterwards (type change or chapter transfer).
interface LateArrival {
  session_id: number;
  application_id: number;
  track: Track;
  reason: "type_change" | "transfer";
  from_track: Track | null;
  from_chapter: string | null;
  at: string;
  name: string | null;
  average: number | null;
  rating_count: number;
}

const EyeIcon = ({ off = false }: { off?: boolean }) => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
    <path
      d="M1.5 12S5.5 4.5 12 4.5 22.5 12 22.5 12 18.5 19.5 12 19.5 1.5 12 1.5 12Z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="12" r="3.2" fill="none" stroke="currentColor" strokeWidth="1.8" />
    {off && <path d="M3 3l18 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />}
  </svg>
);

// Decisions kept in the browser win only where the server has none (always, in a demo).
export const mergeDecisions = (queue: QueueItem[], saved: Record<number, Decision>): QueueItem[] =>
  queue.map((q) => ({ ...q, decision: q.decision ?? saved[q.application_id] ?? null }));

interface Props {
  open: boolean;
  chapterId: number;
  chapterName?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  currentUser?: any;
  onClose: () => void;
}

const quotaOrNull = (v: string): number | null => (v.trim() === "" ? null : Number(v));

// Anchors every step of the wizard to the chapter being reviewed.
const ChapterBadge = ({ name }: { name: string }) => (
  <span className="host-review-chapter" aria-label={`Chapter: ${name}`}>
    <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5z"
      />
    </svg>
    <span className="host-review-chapter-eyebrow" aria-hidden="true">
      Chapter
    </span>
    <span className="host-review-chapter-name" aria-hidden="true">
      {name}
    </span>
  </span>
);

// The live goal for a track: seats filled toward its cohort target, then backup (waitlist) seats.
const SeatMeter = ({
  track,
  tally,
  session,
}: {
  track: Track;
  tally: { accepted: number; waitlisted: number };
  session: SeatTargets;
}) => {
  const target = availableSeats(session, track);
  const backup = backupSeats(session, track);
  const pct = target ? Math.min(100, Math.round((tally.accepted / target) * 100)) : 0;
  const full = target !== null && tally.accepted >= target;
  return (
    <div className="host-review-seat-meter" role="group" aria-label="Seats" aria-live="polite">
      <div className={`host-review-seat-goal${full ? " full" : ""}`}>
        <span className="host-review-seat-label">Seats filled</span>
        <span className="host-review-seat-count">
          <strong>{tally.accepted}</strong>
          {target === null ? " · no limit" : ` / ${target}`}
        </span>
        {target !== null && (
          <span className="host-review-seat-bar" aria-hidden="true">
            <span style={{ width: `${pct}%` }} />
          </span>
        )}
        {target !== null && (
          <small>{full ? "All seats filled — further accepts go to the waitlist" : `${target - tally.accepted} open`}</small>
        )}
      </div>
      {backup > 0 && (
        <div className={`host-review-seat-goal waitlist${tally.waitlisted >= backup ? " full" : ""}`}>
          <span className="host-review-seat-label">Waitlist</span>
          <span className="host-review-seat-count">
            <strong>{tally.waitlisted}</strong> / {backup}
          </span>
        </div>
      )}
    </div>
  );
};

type SeatTrack = "mentee" | "mentor" | "committee";
type CohortCounts = Record<SeatTrack, number>;
const EMPTY_SEATS = {
  mentee: "",
  mentor: "",
  committee: "",
  mentee_backup: "0",
  mentor_backup: "0",
  committee_backup: "0",
};
type SeatForm = typeof EMPTY_SEATS;
const backupKey = (t: SeatTrack) => `${t}_backup` as const;
const SEAT_TRACKS: { key: SeatTrack; label: string; name: string; cohortField: string }[] = [
  { key: "mentee", label: "Mentee seats", name: "Mentee", cohortField: "mentees_count" },
  { key: "mentor", label: "Mentor seats", name: "Mentor", cohortField: "mentors_count" },
  { key: "committee", label: "Committee Member seats", name: "Committee Member", cohortField: "tcm_count" },
];

// Cohort Size stores 0 for "not set", which Host Review treats as no limit (blank).
const seatFromCohort = (n: number) => (n > 0 ? String(n) : "");
const cohortFromSeat = (v: string) => (v.trim() === "" ? 0 : Number(v));
const describeCount = (n: number) => (n > 0 ? String(n) : "not set");

const seatFromQuota = (n: number | null | undefined) => (n === null || n === undefined ? "" : String(n));
// The seats a started session is using, in the Seats step's form shape.
const seatsFromSession = (s: Session): SeatForm => ({
  mentee: seatFromQuota(s.mentee_quota),
  mentor: seatFromQuota(s.mentor_quota),
  committee: seatFromQuota(s.tcm_quota),
  mentee_backup: String(s.mentee_backup_seats ?? 0),
  mentor_backup: String(s.mentor_backup_seats ?? 0),
  committee_backup: String(s.tcm_backup_seats ?? 0),
});
const seatsBody = (seats: SeatForm) => ({
  mentee_quota: quotaOrNull(seats.mentee),
  mentor_quota: quotaOrNull(seats.mentor),
  tcm_quota: quotaOrNull(seats.committee),
  mentee_backup_seats: Number(seats.mentee_backup || 0),
  mentor_backup_seats: Number(seats.mentor_backup || 0),
  tcm_backup_seats: Number(seats.committee_backup || 0),
});
const backupsToForm = (b: Record<BackupField, number>) => ({
  mentee_backup: String(b.mentee_backup_seats),
  mentor_backup: String(b.mentor_backup_seats),
  committee_backup: String(b.tcm_backup_seats),
});

export const cohortChanges = (seats: Record<SeatTrack, string>, cohort: CohortCounts | null) =>
  cohort
    ? SEAT_TRACKS.filter(({ key }) => cohortFromSeat(seats[key]) !== cohort[key]).map(({ key, label }) => ({
        key,
        label,
        from: cohort[key],
        to: cohortFromSeat(seats[key]),
      }))
    : [];

const HostReviewModal = ({ open, chapterId, chapterName, currentUser, onClose }: Props) => {
  const [step, setStep] = useState(0);
  const [mode, setMode] = useState<"formal" | "demo">("demo");
  const [seats, setSeats] = useState(EMPTY_SEATS);
  const [cohort, setCohort] = useState<CohortCounts | null>(null);
  const [confirmingCohort, setConfirmingCohort] = useState(false);
  const [session, setSession] = useState<Session | null>(null);
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [handoff, setHandoff] = useState<Handoff | null>(null);
  const [handedOff, setHandedOff] = useState(false);
  const [authors, setAuthors] = useState<Record<number, string>>({});
  const [viewing, setViewing] = useState<QueueItem | null>(null);
  // The chapter's in-progress formal review, if any. There is only ever one per chapter/year.
  const [active, setActive] = useState<Session | null>(null);
  const [activeSummary, setActiveSummary] = useState<Handoff | null>(null);
  const [lateArrivals, setLateArrivals] = useState<LateArrival[]>([]);
  // Comment authors already revealed (and audit-logged) stay cached; this only controls display.
  const [authorShown, setAuthorShown] = useState<Record<number, boolean>>({});
  // Step 3: a session-only visibility draft. null = keep the chapter's Visibility Toggle settings.
  const [visibilityDraft, setVisibilityDraft] = useState<BlindConfig | null>(null);
  // Every decision made this session, across tracks. Demo queues come back undecided from the
  // server each time, so this is what keeps a demo's choices when moving between steps.
  const decisionsRef = useRef<Record<number, Decision>>({});
  const trackOfRef = useRef<Record<number, Track>>({});
  const seatOfRef = useRef<Record<number, Seat>>({});
  // Demo only: who each seated applicant is and how they rated, so lowering seats can pick who moves.
  const infoOfRef = useRef<Record<number, { name: string; average: number | null }>>({});
  const track = trackAtStep(step);
  const { confirm, confirmDialog } = useConfirm();

  // Each reveal is audit-logged server-side. Hiding and re-showing a revealed author reuses it.
  const reveal = async (rankingId: number) => {
    if (authors[rankingId]) {
      setAuthorShown((s) => ({ ...s, [rankingId]: true }));
      return;
    }
    try {
      const { data } = await axios.post("/api/rater_admin/comment_reveal", { ranking_id: rankingId });
      setAuthors((a) => ({ ...a, [rankingId]: data.author || "Unknown" }));
      setAuthorShown((s) => ({ ...s, [rankingId]: true }));
    } catch (err) {
      toast.error(apiError(err, "Unable to reveal author"));
    }
  };
  const hideAuthor = (rankingId: number) => setAuthorShown((s) => ({ ...s, [rankingId]: false }));

  const isDemo = session?.mode === "demo";

  useEffect(() => {
    if (!open || !chapterId) return;
    let cancelled = false;
    axios
      .get("/api/host_review/sessions/active", { params: { chapter_id: chapterId } })
      .then(async ({ data }) => {
        if (cancelled || !data?.id) return;
        setActive(data);
        setMode("formal");
        // The same summary the handoff step shows, as it stands now. Optional: Step 1 still works without it.
        try {
          const summary = await axios.get(`/api/host_review/${data.id}/handoff`);
          if (!cancelled && summary.data?.tracks) setActiveSummary(summary.data);
        } catch {
          /* the progress strip is enough to continue */
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [open, chapterId]);

  // Applicants who reached the chapter's completed review afterwards and still need a decision.
  const loadLateArrivals = useCallback(async () => {
    try {
      const { data } = await axios.get<LateArrival[]>("/api/host_review/late_arrivals", {
        params: { chapter_id: chapterId },
      });
      setLateArrivals(Array.isArray(data) ? data : []);
    } catch {
      setLateArrivals([]);
    }
  }, [chapterId]);

  useEffect(() => {
    if (open && chapterId) loadLateArrivals();
  }, [open, chapterId, loadLateArrivals]);

  const decideLate = async (item: LateArrival, decision: Decision) => {
    const who = item.name ?? "this applicant";
    const step = TRACKS.find((t) => t.id === item.track)?.label ?? item.track;
    const ok = await confirm({
      title: decision === "accepted" ? "Accept late arrival?" : "Reject late arrival?",
      message:
        decision === "accepted"
          ? `Accept ${who} into ${step}? They take an open seat, or the waitlist once seats are full, and the HR Review handoff is updated.`
          : `Reject ${who}? The HR Review handoff is updated.`,
      confirmLabel: decision === "accepted" ? "Accept" : "Reject",
      tone: decision === "accepted" ? "primary" : "danger",
    });
    if (!ok) return;
    setBusy(true);
    try {
      const { data } = await axios.patch(`/api/host_review/${item.session_id}/late_arrivals/${item.application_id}`, {
        decision,
      });
      toast.success(
        decision === "rejected"
          ? `${item.name ?? "Applicant"} rejected.`
          : data?.status_id === STATUS_WAITLISTED
            ? `${item.name ?? "Applicant"} added to the waitlist: every seat is filled.`
            : `${item.name ?? "Applicant"} accepted.`
      );
      await loadLateArrivals();
    } catch (err) {
      toast.error(apiError(err, "Unable to save decision"));
    } finally {
      setBusy(false);
    }
  };

  // List endpoint on purpose: GET /api/cohort_size/:chapterId creates a missing row.
  useEffect(() => {
    if (!open || !chapterId) return;
    let cancelled = false;
    axios
      .get("/api/cohort_size")
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then(({ data }: { data: any[] }) => {
        if (cancelled) return;
        const row = (data || []).find((r) => r.chapter_id === chapterId);
        const counts: CohortCounts = {
          mentee: row?.mentees_count ?? 0,
          mentor: row?.mentors_count ?? 0,
          committee: row?.tcm_count ?? 0,
        };
        setCohort(counts);
        setSeats((s) => ({
          ...s,
          mentee: seatFromCohort(counts.mentee),
          mentor: seatFromCohort(counts.mentor),
          committee: seatFromCohort(counts.committee),
        }));
      })
      .catch(() => {
        if (!cancelled) setCohort(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open, chapterId]);

  const changes = cohortChanges(seats, cohort);
  // Back from Visibility re-opens Seats for the session already under way, rather than starting another.
  const editingSeats = !!session;

  const requestStart = () => {
    if (mode === "formal" && changes.length > 0) setConfirmingCohort(true);
    else if (editingSeats) saveSeats(false);
    else start(false);
  };

  const updateCohortSize = async () => {
    try {
      await axios.put(`/api/cohort_size/${chapterId}`, {
        mentees_count: cohortFromSeat(seats.mentee),
        mentors_count: cohortFromSeat(seats.mentor),
        tcm_count: cohortFromSeat(seats.committee),
      });
      setCohort({
        mentee: cohortFromSeat(seats.mentee),
        mentor: cohortFromSeat(seats.mentor),
        committee: cohortFromSeat(seats.committee),
      });
      toast.success("Cohort Size updated");
    } catch (err) {
      toast.error(apiError(err, "Seats saved, but Cohort Size could not be updated"));
    }
  };

  // Lowering seats below what a track has accepted moves the lowest-rated to the waitlist, so the
  // host sees exactly who moves (and any waitlist-seat raise) before anything is saved.
  const confirmSeatPlan = (plan: Partial<SeatPlan>) => {
    const moved = plan.moved ?? [];
    const raised = plan.backup_raised ?? [];
    if (moved.length === 0 && raised.length === 0) return Promise.resolve(true);
    const n = moved.length;
    return confirm({
      title: n > 0 ? `Move ${n} to the waitlist?` : "Raise waitlist seats?",
      message: (
        <div className="seat-plan">
          {n > 0 && (
            <p>
              {n === 1 ? "This seat target is" : "These seat targets are"} below what is already accepted. The
              lowest-rated move to the front of the waitlist:
            </p>
          )}
          {TRACKS.filter(({ id }) => moved.some((m) => m.track === id)).map(({ id, label }) => (
            <div key={id} className="seat-plan-track">
              <strong>{label}</strong>
              <ol>
                {moved
                  .filter((m) => m.track === id)
                  .map((m) => (
                    <li key={m.application_id}>
                      Waitlist #{m.waitlist_position}: {m.name}{" "}
                      <span className="seat-plan-average">
                        ({m.average === null ? "unrated" : `avg ${m.average.toFixed(2)}`})
                      </span>
                    </li>
                  ))}
              </ol>
            </div>
          ))}
          {raised.length > 0 && plan.backups && (
            <p>
              Waitlist seats rise so the waitlist fits:{" "}
              {TRACKS.filter(({ id }) => raised.includes(id))
                .map(({ id, label }) => `${label} to ${plan.backups?.[BACKUP_FIELDS[id]]}`)
                .join(", ")}
              .
            </p>
          )}
        </div>
      ),
      confirmLabel: n > 0 ? "Move to waitlist" : "Raise waitlist seats",
      tone: "primary",
    });
  };

  // A demo's seats live only here. A formal session's are saved on the server, after a preview of
  // anyone the new targets move to the waitlist.
  const saveSeats = async (updateCohort: boolean) => {
    if (!session) return;
    const body = seatsBody(seats);
    const unchanged = (Object.keys(body) as (keyof typeof body)[]).every((k) => body[k] === session[k]);
    if (unchanged) {
      setConfirmingCohort(false);
      setStep(2);
      return;
    }
    if (isDemo) {
      const plan = planDemotions(seatOfRef.current, trackOfRef.current, infoOfRef.current, body);
      setConfirmingCohort(false);
      if (!(await confirmSeatPlan(plan))) return;
      for (const m of plan.moved) seatOfRef.current[m.application_id] = "waitlisted";
      setSession({ ...session, ...body, ...plan.backups });
      setSeats((s) => ({ ...s, ...backupsToForm(plan.backups) }));
      setStep(2);
      return;
    }
    setBusy(true);
    try {
      const url = `/api/host_review/${session.id}/seats`;
      const { data: plan } = await axios.patch(url, { ...body, preview: true });
      setConfirmingCohort(false);
      if (!(await confirmSeatPlan(plan))) return;
      const { data } = await axios.patch(url, body);
      setSession(data);
      setActive(data);
      setStep(2);
      if (updateCohort) await updateCohortSize();
    } catch (err) {
      setConfirmingCohort(false);
      toast.error(apiError(err, "Unable to update seats"));
    } finally {
      setBusy(false);
    }
  };

  const editSeats = () => {
    if (session) setSeats(seatsFromSession(session));
    setStep(1);
  };

  const start = async (updateCohort: boolean) => {
    setBusy(true);
    try {
      const body = { chapter_id: chapterId, mode, ...seatsBody(seats) };
      const { data } = await axios.post("/api/host_review/sessions", body);
      setSession(data);
      setConfirmingCohort(false);
      setStep(2);
      if (updateCohort && data.mode === "formal") await updateCohortSize();
    } catch (err) {
      setConfirmingCohort(false);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const existing = (err as any)?.response?.data?.session;
      if (existing) {
        resume(existing);
      } else {
        toast.error(apiError(err, "Unable to start session"));
      }
    } finally {
      setBusy(false);
    }
  };

  const fetchQueue = useCallback(
    async (s: Session, t: Track): Promise<QueueItem[]> => {
      // A formal session's visibility lives on the server; a demo's only exists here, so it travels with the request.
      const demoParams =
        s.mode === "demo"
          ? {
              ...(s.state?.blind ? { blind: JSON.stringify(s.state.blind) } : {}),
              ...(s.state?.reveal?.on ? { reveal: "true" } : {}),
            }
          : {};
      const { data } = await axios.get(`/api/host_review/${s.id}/queue`, {
        params: { track: t, chapter_id: chapterId, ...demoParams },
      });
      return mergeDecisions(data.queue, decisionsRef.current);
    },
    [chapterId]
  );

  // quiet: refresh in place (no "Loading…", no collapsing) so the list doesn't blink.
  const loadTrack = useCallback(
    async (s: Session, t: Track, quiet = false) => {
      if (!quiet) {
        setBusy(true);
        setQueue([]);
        setExpanded(null);
      }
      try {
        setQueue(await fetchQueue(s, t));
      } catch (err) {
        toast.error(apiError(err, "Unable to load queue"));
      } finally {
        if (!quiet) setBusy(false);
      }
    },
    [fetchQueue]
  );

  const fetchHandoff = async (s: Session): Promise<Handoff> => {
    if (s.mode === "demo") return demoHandoff(decisionsRef.current, trackOfRef.current, seatOfRef.current, s);
    const { data } = await axios.get(`/api/host_review/${s.id}/handoff`);
    return data;
  };

  // The next step's data is fetched while the current step stays on screen, then the step and
  // its data swap in one render, so moving between tracks doesn't flash an empty "Loading…" list.
  const goToStep = async (next: number, s: Session | null = session) => {
    const t = trackAtStep(next);
    if (!s || (!t && next !== FINISH_STEP)) {
      setStep(next);
      return;
    }
    setBusy(true);
    try {
      if (t) {
        const nextQueue = await fetchQueue(s, t);
        setExpanded(null);
        setQueue(nextQueue);
      } else {
        setHandoff(await fetchHandoff(s));
      }
      setStep(next);
    } catch (err) {
      toast.error(apiError(err, t ? "Unable to load queue" : "Unable to load the HR Review handoff"));
    } finally {
      setBusy(false);
    }
  };

  // A formal review is THE year's cohort review: returning to it picks up where it was left off.
  // Back from there still reaches Visibility and Seats.
  const resume = (s: Session) => {
    setSession(s);
    setActive(s);
    setMode("formal");
    goToStep(resumeStep(s), s);
  };

  // Applies Step 3's session-only override (if any) before the first track. The chapter's own
  // Visibility Toggle settings are never changed.
  const beginReview = async () => {
    if (!session) return;
    let s = session;
    if (visibilityDraft && !sameBlind(visibilityDraft, session.state?.blind)) {
      if (isDemo) {
        s = { ...session, state: { ...session.state, blind: visibilityDraft } };
      } else {
        setBusy(true);
        try {
          const { data } = await axios.patch(`/api/host_review/${session.id}/visibility`, { blind: visibilityDraft });
          s = data;
        } catch (err) {
          toast.error(apiError(err, "Unable to change visibility for this review"));
          return;
        } finally {
          setBusy(false);
        }
      }
      setSession(s);
    }
    // Coming back to Visibility mid-review returns to the track that was open, not to Mentees.
    goToStep(isDemo ? FIRST_TRACK_STEP : Math.max(resumeStep(s), FIRST_TRACK_STEP), s);
  };

  // Sticky for the rest of the session. Formal sessions record who flipped it and when; a demo records nothing.
  // Applied optimistically and refreshed in place, so the list swaps labels for names without reloading.
  const setRevealed = async (on: boolean) => {
    if (!session) return;
    const prior = session.state?.reveal?.history ?? [];
    let s: Session = {
      ...session,
      state: { ...session.state, reveal: { on, history: [...prior, { on, at: new Date().toISOString() }] } },
    };
    setSession(s);
    if (!isDemo) {
      try {
        const { data } = await axios.patch(`/api/host_review/${session.id}/reveal`, { revealed: on });
        s = data;
        setSession(s);
      } catch (err) {
        setSession(session);
        toast.error(apiError(err, "Unable to change identity reveal"));
        return;
      }
    }
    if (track) loadTrack(s, track, true);
  };

  const decide = async (item: QueueItem, decision: Decision): Promise<boolean> => {
    if (!session) return false;
    let statusId: number | null | undefined = item.status_id;
    if (isDemo) {
      if (decision === "accepted" && track) {
        const seat = routeAccept(item.application_id, track, seatOfRef.current, trackOfRef.current, session);
        if (!seat) {
          toast.error("All seats and backup seats are full");
          return false;
        }
        seatOfRef.current[item.application_id] = seat;
        infoOfRef.current[item.application_id] = { name: item.name ?? item.label, average: item.average };
      } else {
        delete seatOfRef.current[item.application_id];
      }
    } else {
      try {
        const { data } = await axios.patch(`/api/host_review/${session.id}/decision`, {
          application_id: item.application_id,
          decision,
        });
        statusId = data?.status_id ?? statusId;
      } catch (err) {
        toast.error(apiError(err, "Unable to save decision"));
        return false;
      }
    }
    decisionsRef.current[item.application_id] = decision;
    if (track) trackOfRef.current[item.application_id] = track;
    setQueue((q) =>
      q.map((x) => (x.application_id === item.application_id ? { ...x, decision, status_id: statusId } : x))
    );
    return true;
  };

  // Accepts everyone not already rejected or accepted, in rating order, so the highest-rated take
  // the seats first and the rest spill onto the waitlist. Stops at the first refusal (seats full).
  const acceptAll = async () => {
    if (!session) return;
    const pending = queue.filter((q) => q.decision !== "rejected" && q.decision !== "accepted");
    if (pending.length === 0) return;
    if (!isDemo) {
      const ok = await confirm({
        title: `Accept ${pending.length} ${pending.length === 1 ? "applicant" : "applicants"}?`,
        message: `Everyone on this list not already rejected will be accepted. Seats fill in rating order, then the waitlist. Each decision is saved to the application.`,
        confirmLabel: "Accept all",
        tone: "success",
      });
      if (!ok) return;
    }
    setBusy(true);
    try {
      for (const item of pending) {
        if (!(await decide(item, "accepted"))) break;
      }
    } finally {
      setBusy(false);
    }
  };

  const rejectRemaining = async () => {
    if (!session) return;
    if (isDemo) {
      setQueue((q) =>
        q.map((x) => {
          if (x.decision) return x;
          decisionsRef.current[x.application_id] = "rejected";
          trackOfRef.current[x.application_id] = "mentee";
          return { ...x, decision: "rejected" };
        })
      );
      return;
    }
    const ok = await confirm({
      title: "Reject remaining mentees?",
      message: "Every mentee who has not been decided will be rejected. This cannot be undone.",
      confirmLabel: "Reject remaining",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await axios.post(`/api/host_review/${session.id}/batch_reject`);
      await loadTrack(session, "mentee");
    } catch (err) {
      toast.error(apiError(err, "Unable to reject remaining applicants"));
    }
  };

  // Formal sessions record each finished track so completion can be enforced server-side too.
  const endTrack = async (): Promise<boolean> => {
    if (!session || !track || isDemo) return true;
    try {
      await axios.patch(`/api/host_review/${session.id}/end_track`, { track });
      return true;
    } catch (err) {
      toast.error(apiError(err, "Unable to finish this track"));
      return false;
    }
  };

  const nextTrack = async () => {
    setBusy(true);
    const ok = await endTrack();
    if (ok) await goToStep(step + 1);
    else setBusy(false);
  };

  // Formal: ratings lock and the proposed cohort is recorded for HR Eligibility Review (#44).
  // Nothing is sent to applicants; offers wait until HR clears the cohort.
  const finish = async () => {
    if (!session) return;
    if (isDemo) {
      setHandedOff(true);
      return;
    }
    setBusy(true);
    try {
      await axios.patch(`/api/host_review/${session.id}/lock_ratings`);
      const { data } = await axios.patch(`/api/host_review/${session.id}/complete`);
      if (data.handoff) setHandoff(data.handoff);
      setHandedOff(true);
      toast.success("Host Review complete. The proposed cohort was sent to HR Review.");
    } catch (err) {
      toast.error(apiError(err, "Unable to complete session"));
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    setStep(0);
    setMode("demo");
    setSeats(EMPTY_SEATS);
    setConfirmingCohort(false);
    setSession(null);
    setQueue([]);
    setHandoff(null);
    setHandedOff(false);
    setViewing(null);
    setVisibilityDraft(null);
    setActive(null);
    setActiveSummary(null);
    setAuthorShown({});
    decisionsRef.current = {};
    trackOfRef.current = {};
    seatOfRef.current = {};
    infoOfRef.current = {};
    onClose();
  };

  const decided = queue.filter((q) => q.decision).length;
  const allDecided = decided === queue.length;
  const acceptable = queue.filter((q) => q.decision !== "rejected" && q.decision !== "accepted").length;
  const tally = seatTally(queue, isDemo ? seatOfRef.current : null);
  const blindConfig = session?.state?.blind;
  const revealed = !!session?.state?.reveal?.on;
  const blind = !!(blindConfig?.enabled && blindConfig.mask_name) && !revealed;
  const mask = revealed ? null : blindMaskFrom(blindConfig);
  const everRevealed = (session?.state?.reveal?.history ?? []).some((h) => h.on);
  const shownBlind = visibilityDraft ?? blindConfig;
  const reviewUnderway = isDemo
    ? Object.keys(decisionsRef.current).length > 0
    : TRACKS.some(({ id }) => session?.state?.tracks?.[id]?.order);
  // The dialog follows the live queue row, so a reveal updates its title in place.
  const viewingItem = viewing ? queue.find((q) => q.application_id === viewing.application_id) ?? viewing : null;
  const sessionLabel = session
    ? isDemo
      ? "Demo"
      : `Formal review${session.year ? `, ${session.year}` : ""}`
    : null;
  const revealToggle = blindConfig?.enabled ? (
    <label className="application-rater-toggle host-review-reveal-toggle">
      <input type="checkbox" checked={revealed} onChange={(e) => setRevealed(e.target.checked)} /> Reveal identities
      {isDemo ? " (demo: not recorded)" : " (recorded in the audit log and the HR Review handoff)"}
    </label>
  ) : null;

  return (
    <>
    <PrismDialog open={open} close-method="user" setAriaLabel="Host Review" onPrismClose={close}>
      <span slot="header" className="host-review-header">
        {chapterName && <ChapterBadge name={chapterName} />}
        <span>
          {confirmingCohort
            ? "Host Review — Update Cohort Size?"
            : `Host Review — Step ${step + 1} of ${STEPS.length}: ${STEPS[step]}${sessionLabel ? ` (${sessionLabel})` : ""}`}
        </span>
      </span>
      <div className="host-review-body">
        {step === 0 && (
          <div>
            <PrismBanner tone="info">
              A demo walks through the process with real ratings but never changes any application. A formal
              session records decisions and updates application statuses.
            </PrismBanner>
            <label className="application-rater-toggle">
              <input type="radio" checked={mode === "demo"} onChange={() => setMode("demo")} /> Demo (nothing is saved)
            </label>
            <label className="application-rater-toggle">
              <input type="radio" checked={mode === "formal"} onChange={() => setMode("formal")} />{" "}
              {active ? "Continue the formal review" : "Formal session"}
            </label>
            {mode === "formal" && active && (
              <section className="host-review-resume" aria-label="Formal review in progress">
                <div className="host-review-badges">
                  <span className="host-review-badge primary">Formal review {active.year}</span>
                  {active.created_at && (
                    <span className="host-review-badge">
                      Started {new Date(active.created_at).toLocaleDateString()}
                    </span>
                  )}
                  <span className="host-review-badge">
                    {decisionCount(active)} decision{decisionCount(active) === 1 ? "" : "s"}
                  </span>
                </div>
                <ReviewProgress
                  tracks={TRACKS}
                  state={active.state?.tracks}
                  current={Math.max(resumeStep(active) - FIRST_TRACK_STEP, 0)}
                />
                {activeSummary && <CohortSummary handoff={activeSummary} tracks={TRACKS} caption="Cohort so far" />}
                <p className="host-review-resume-note">
                  Continue picks up at <strong>{STEPS[resumeStep(active)]}</strong>. Back from there lets you
                  adjust seats and visibility.
                </p>
              </section>
            )}
            {lateArrivals.length > 0 && (
              <section className="host-review-late" aria-label="Late arrivals">
                <h3>Late arrivals: decision needed</h3>
                <p>
                  These applicants changed type or chapter after the formal review was completed. Accept or reject
                  each one; seats and the waitlist work as they did in the room, and the HR Review handoff is updated.
                </p>
                <ul>
                  {lateArrivals.map((l) => (
                    <li key={`${l.session_id}-${l.application_id}`}>
                      <span>
                        <strong>{l.name ?? `Application #${l.application_id}`}</strong>{" "}
                        <span className="host-review-moved">
                          {movedInLabel(l)} → {TRACKS.find((t) => t.id === l.track)?.label ?? l.track}
                        </span>{" "}
                        <small className="waitlist-muted">
                          {l.average === null ? "Unrated" : `Avg ${l.average.toFixed(2)}`} ({l.rating_count} ratings)
                        </small>
                      </span>
                      <span className="host-review-late-actions">
                        <PrismButton size="sm" tone="success" disabled={busy} onPrismClick={() => decideLate(l, "accepted")}>
                          Accept
                        </PrismButton>
                        <PrismButton
                          size="sm"
                          tone="secondary"
                          display="outline"
                          disabled={busy}
                          onPrismClick={() => decideLate(l, "rejected")}
                        >
                          Reject
                        </PrismButton>
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        )}

        {step === 1 && confirmingCohort && (
          <div className="host-review-cohort-confirm">
            <p>
              {editingSeats ? "Saving these seats" : "Starting this formal session"} will also update this chapter's
              Cohort Size:
            </p>
            <ul>
              {changes.map((c) => (
                <li key={c.key}>
                  {c.label}: {describeCount(c.from)} → <strong>{describeCount(c.to)}</strong>
                </li>
              ))}
            </ul>
            <p>Cohort Size is shown on the application form and in Cohort Progress.</p>
          </div>
        )}

        {step === 1 && !confirmingCohort && (
          <div className="host-review-seats">
            <p>
              {editingSeats
                ? "These are the seats this review is using."
                : "Seat counts come from this chapter's Cohort Size."}{" "}
              Leave a seat count blank for no limit. Waitlist seats cap each track&apos;s waitlist.
            </p>
            {mode === "demo" ? (
              <PrismBanner tone="info">
                Demo: changes here apply to this demo only. Cohort Size is not updated.
              </PrismBanner>
            ) : (
              <PrismBanner tone="info">
                Formal session: changed seat counts are also saved to Cohort Size, after you confirm.
                {editingSeats &&
                  " Lowering seats below the applicants already accepted moves the lowest-rated to the front of the waitlist; you'll see who before anything is saved."}
              </PrismBanner>
            )}
            <div className="host-review-seat-grid">
              {SEAT_TRACKS.map(({ key, label, name }) => (
                <div key={key} className="host-review-seat-row">
                  <label>
                    {label}
                    <input
                      type="number"
                      min={0}
                      placeholder="No limit"
                      value={seats[key]}
                      onChange={(e) => setSeats({ ...seats, [key]: e.target.value })}
                    />
                  </label>
                  <label>
                    {name} waitlist seats
                    <input
                      type="number"
                      min={0}
                      placeholder="0"
                      value={seats[backupKey(key)]}
                      onChange={(e) => setSeats({ ...seats, [backupKey(key)]: e.target.value })}
                    />
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="host-review-visibility">
            <p>
              Blind review is <strong>{shownBlind?.enabled ? "on" : "off"}</strong> for this review
              {shownBlind?.enabled
                ? ": names are replaced by labels and hidden fields are left out of applications."
                : "."}
            </p>
            {shownBlind?.enabled && (
              <p>
                Hidden:{" "}
                {MASK_FIELDS.filter((f) => shownBlind[f.key])
                  .map((f) => f.label)
                  .join(", ") || "nothing"}
              </p>
            )}
            <label className="application-rater-toggle">
              <input
                type="checkbox"
                checked={!!visibilityDraft}
                onChange={(e) => setVisibilityDraft(e.target.checked ? { ...(blindConfig ?? {}) } : null)}
              />{" "}
              Change visibility for this review only
            </label>
            {visibilityDraft ? (
              <fieldset className="host-review-visibility-override">
                <legend>This review only — the chapter&apos;s Visibility Toggle settings are not changed</legend>
                <label className="application-rater-toggle">
                  <input
                    type="checkbox"
                    checked={!!visibilityDraft.enabled}
                    onChange={(e) => setVisibilityDraft({ ...visibilityDraft, enabled: e.target.checked })}
                  />{" "}
                  Blind review on
                </label>
                {MASK_FIELDS.map((f) => (
                  <label key={f.key} className="application-rater-toggle">
                    <input
                      type="checkbox"
                      disabled={!visibilityDraft.enabled}
                      checked={!!visibilityDraft[f.key]}
                      onChange={(e) => setVisibilityDraft({ ...visibilityDraft, [f.key]: e.target.checked })}
                    />{" "}
                    Hide {f.label.toLowerCase()}
                  </label>
                ))}
                {!isDemo && <p>The change is recorded in the audit log and the HR Review handoff.</p>}
              </fieldset>
            ) : (
              <p>Unchanged: this review uses the chapter&apos;s Visibility Toggle settings.</p>
            )}
          </div>
        )}

        {track && session && (
          <div>
            {revealToggle}
            {queue.length > 0 && <SeatMeter track={track} tally={tally} session={session} />}
            <p className="host-review-progress">
              {busy && queue.length === 0
                ? "Loading…"
                : queue.length === 0
                  ? `No ${STEPS[step].toLowerCase()} to review for this chapter.`
                  : `${decided} of ${queue.length} decided${allDecided ? "" : " — decide every applicant to continue"}`}
            </p>
            {queue.length > 0 && (
              <div className="host-review-bulk-actions">
                <PrismButton
                  size="sm"
                  tone="success"
                  display="outline"
                  disabled={busy || acceptable === 0}
                  onPrismClick={acceptAll}
                >
                  <span>{acceptable > 0 ? `Accept all (${acceptable})` : "Accept all"}</span>
                </PrismButton>
                {track === "mentee" && !allDecided && (
                  <PrismButton size="sm" tone="danger" display="outline" disabled={busy} onPrismClick={rejectRemaining}>
                    Reject all remaining mentees
                  </PrismButton>
                )}
              </div>
            )}
            <ol className="host-review-queue">
              {queue.map((item) => (
                <li key={item.application_id} className={`host-review-item ${item.decision ?? ""}`}>
                  <div className="host-review-item-row">
                    <strong>{blind ? item.label : item.name ?? item.label}</strong>
                    {item.moved_in && <span className="host-review-moved">{movedInLabel(item.moved_in)}</span>}
                    <span>
                      {item.average === null ? "Unrated" : `Avg ${item.average.toFixed(2)}`} ({item.rating_count}{" "}
                      ratings)
                    </span>
                    <PrismButton size="sm" tone="secondary" display="outline" onPrismClick={() => setViewing(item)}>
                      View application
                    </PrismButton>
                    {/* Dynamic labels sit in a span: React would otherwise replace the button's
                        own rendered markup when the text changes. */}
                    <PrismButton
                      size="sm"
                      tone="secondary"
                      display="outline"
                      disabled={item.comments.length === 0}
                      onPrismClick={() => setExpanded(expanded === item.application_id ? null : item.application_id)}
                    >
                      <span>
                        {item.comments.length === 0
                          ? "No comments"
                          : expanded === item.application_id
                            ? "Hide comments"
                            : `Comments (${item.comments.length})`}
                      </span>
                    </PrismButton>
                    <PrismButton
                      size="sm"
                      tone={item.decision === "accepted" ? "success" : "secondary"}
                      display={item.decision === "accepted" ? "fill" : "outline"}
                      onPrismClick={() => decide(item, "accepted")}
                    >
                      Accept
                    </PrismButton>
                    <PrismButton
                      size="sm"
                      tone={item.decision === "rejected" ? "danger" : "secondary"}
                      display={item.decision === "rejected" ? "fill" : "outline"}
                      onPrismClick={() => decide(item, "rejected")}
                    >
                      Reject
                    </PrismButton>
                  </div>
                  {expanded === item.application_id && (
                    <div className="host-review-comments">
                      <div className="host-review-wordmap">
                        <WordMap comments={item.comments.map((c) => c.text)} />
                      </div>
                      <ul className="host-review-comment-list">
                        {item.comments.map((c) => {
                          const author = authorShown[c.ranking_id] ? authors[c.ranking_id] : undefined;
                          return (
                            <li key={c.ranking_id} className="host-review-comment">
                              <span className="host-review-comment-text">{c.text}</span>
                              {author ? (
                                <button
                                  type="button"
                                  className="host-review-author shown"
                                  onClick={() => hideAuthor(c.ranking_id)}
                                  aria-label={`Hide author ${author}`}
                                  title="Hide author"
                                >
                                  <EyeIcon off />
                                  <span>{author}</span>
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className="host-review-author"
                                  onClick={() => reveal(c.ranking_id)}
                                  aria-label="Reveal author"
                                  title="Reveal author"
                                >
                                  <EyeIcon />
                                </button>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </li>
              ))}
            </ol>
          </div>
        )}

        {step === FINISH_STEP && (
          <div className="host-review-handoff">
            {handedOff ? (
              <PrismBanner tone="success">
                {isDemo
                  ? "Demo finished. Nothing was saved and nothing was sent to HR Review."
                  : "Host Review complete. Ratings are locked and the proposed cohort is waiting in HR Eligibility Review. No offers or emails were sent."}
              </PrismBanner>
            ) : (
              <>
                <PrismBanner tone="info">
                  <strong>Next phase: HR Eligibility Review.</strong> Only the <strong>Mentees</strong> row goes to
                  HR, who look up each accepted and waitlisted mentee to confirm eligibility before any offer is made.
                  Mentors and Committee Members already work with their managers to support the program, so HR
                  doesn&apos;t check them; their results carry forward to the later steps that run the program.
                  Rejected applicants are not passed along.
                </PrismBanner>
                {isDemo ? (
                  <p>
                    Demo: nothing is saved or sent to HR. In a formal session, this is where the committee&apos;s
                    results would be handed off.
                  </p>
                ) : (
                  <p>
                    Sending to HR Review locks ratings for this chapter and completes this Host Review, which cannot
                    be reopened. No offers or emails are sent at this step.
                  </p>
                )}
              </>
            )}
            {everRevealed && (
              <p className="host-review-reveal-note">
                {isDemo
                  ? "Identities were revealed during this demo. Nothing was recorded."
                  : "Identities were revealed during this review. Who revealed them, and when, goes to HR Review with the cohort."}
              </p>
            )}
            {handoff ? (
              <CohortSummary
                handoff={handoff}
                tracks={TRACKS}
                toHr="mentee"
                caption={handedOff ? "Sent to HR Review" : "Proposed cohort"}
              />
            ) : (
              busy && <p>Loading…</p>
            )}
          </div>
        )}
      </div>

      {/* One stable slotted wrapper: the dialog relocates slotted nodes, so adding or removing
          slotted buttons per step directly would make React's removeChild throw. */}
      <div slot="footer" className="host-review-footer">
        {step === 2 || track ? (
          <PrismButton tone="secondary" display="outline" onPrismClick={close}>
            Close
          </PrismButton>
        ) : null}
        {step === 0 && (
          <>
            <PrismButton tone="secondary" display="outline" onPrismClick={close}>
              Cancel
            </PrismButton>
            <PrismButton onPrismClick={() => (mode === "formal" && active ? resume(active) : setStep(1))}>
              <span>{mode === "formal" && active ? "Continue" : "Next"}</span>
            </PrismButton>
          </>
        )}
        {step === 1 && confirmingCohort && (
          <>
            <PrismButton tone="secondary" display="outline" onPrismClick={() => setConfirmingCohort(false)}>
              Back
            </PrismButton>
            <PrismButton loading={busy} onPrismClick={() => (editingSeats ? saveSeats(true) : start(true))}>
              {editingSeats ? "Update and save" : "Update and start"}
            </PrismButton>
          </>
        )}
        {step === 1 && !confirmingCohort && (
          <>
            <PrismButton tone="secondary" display="outline" onPrismClick={close}>
              {editingSeats ? "Close" : "Cancel"}
            </PrismButton>
            <PrismButton
              tone="secondary"
              display="outline"
              disabled={busy}
              onPrismClick={() => setStep(editingSeats ? 2 : 0)}
            >
              Back
            </PrismButton>
            <PrismButton loading={busy} disabled={busy} onPrismClick={requestStart}>
              {editingSeats ? "Save and continue" : "Start"}
            </PrismButton>
          </>
        )}
        {step === 2 && (
          <>
            <PrismButton tone="secondary" display="outline" disabled={busy} onPrismClick={editSeats}>
              Back
            </PrismButton>
            <PrismButton loading={busy} disabled={busy} onPrismClick={beginReview}>
              {reviewUnderway ? "Continue review" : "Begin review"}
            </PrismButton>
          </>
        )}
        {track && (
          <PrismButton tone="secondary" display="outline" disabled={busy} onPrismClick={() => goToStep(step - 1)}>
            Back
          </PrismButton>
        )}
        {track && (
          <PrismButton loading={busy} disabled={busy || !allDecided} onPrismClick={nextTrack}>
            <span>{`Next: ${STEPS[step + 1]}`}</span>
          </PrismButton>
        )}
        {step === FINISH_STEP && !handedOff && (
          <>
            <PrismButton tone="secondary" display="outline" disabled={busy} onPrismClick={close}>
              Cancel
            </PrismButton>
            <PrismButton
              tone="secondary"
              display="outline"
              disabled={busy}
              onPrismClick={() => goToStep(FINISH_STEP - 1)}
            >
              Back
            </PrismButton>
            <PrismButton loading={busy} disabled={busy || (!isDemo && !handoff)} onPrismClick={finish}>
              <span>{isDemo ? "End demo" : "Send to HR Review"}</span>
            </PrismButton>
          </>
        )}
        {step === FINISH_STEP && handedOff && <PrismButton onPrismClick={close}>Done</PrismButton>}
      </div>
    </PrismDialog>

    {/* A sibling dialog (not nested) so it stacks above Host Review via showModal(). */}
    <PrismDialog
      className="host-review-application-dialog"
      open={!!viewing}
      close-method="user"
      setAriaLabel="Application"
      onPrismClose={() => setViewing(null)}
    >
      <span slot="header">
        {viewingItem ? (blind ? viewingItem.label : viewingItem.name ?? viewingItem.label) : "Application"}
      </span>
      <div className="host-review-application">
        {viewing && revealToggle}
        {viewing && (
          <SubmittedApp
            params={{ application_id: viewing.application_id }}
            currentUser={currentUser}
            blindMask={mask}
            blindLabel={viewing.label}
            hideHeader
          />
        )}
      </div>
      <div slot="footer" className="host-review-footer">
        <PrismButton tone="secondary" display="outline" onPrismClick={() => setViewing(null)}>
          Close
        </PrismButton>
      </div>
    </PrismDialog>

    {/* Opened last, so it stacks above the Host Review (and Application) dialogs. */}
    {confirmDialog}
    </>
  );
};

export default HostReviewModal;
